/**
 * Shared Video Schedule API Module
 * Used by createVideoPage.js and hangout upgrade-sprite modal
 */

import { CREATE_SPRITE_WORKER_URL, USER_WORKER_URL, isSfwSite } from '../config.js';
import { storage } from '../utils/storage.js';
import { parseMembershipUsageResponse } from '../utils/membershipUsage.js';
import { initOAuthManager } from '../utils/oauthManager.js';

/**
 * Check if user has enough credits
 * @param {number} totalCost - Total cost for the operation
 * @param {number|null} availableCredits - Optional override for available credits
 * @returns {boolean}
 */
export const hasEnoughCredits = (totalCost, availableCredits = null) => {
  const available = availableCredits ?? getUserCredits();
  return totalCost <= available;
};

/**
 * Get user's available sprite credits
 * @returns {number}
 */
export const getUserCredits = () => {
  const userData = storage.get('userData');
  return userData?.sprite_credits ?? 0;
};

/**
 * Format credits display string
 * @param {number} totalCost - Total cost
 * @param {number|null} availableCredits - Optional available credits override
 * @returns {string}
 */
export const formatCreditsLine = (totalCost, availableCredits = null) => {
  const available = availableCredits ?? getUserCredits();
  return `This will use ${totalCost} / ${available} credits`;
};

/**
 * Sync membership usage data from backend and update storage.
 * Dispatches both membershipDataSynced and spriteCreditsUpdated for compatibility.
 * @returns {Promise<void>}
 */
export const syncMembershipUsage = async () => {
  try {
    const token = await getAuthToken();
    if (!token) return;

    const response = await fetch(`${USER_WORKER_URL}/membership-usage?funding=${isSfwSite() ? 'sfw_host' : 'crypto'}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) return;

    const result = await response.json();
    const usageData = parseMembershipUsageResponse(result);
    if (!usageData) return;

    const currentUserData = storage.get('userData') || {};
    const remainingSpriteCredits = usageData.remaining.sprite_credits;

    const updatedUserData = {
      ...currentUserData,
      current_tier: usageData.current_tier,
      crypto_access: usageData.crypto_access === true,
      sprite_credits: usageData.crypto_access ? usageData.crypto_balance : remainingSpriteCredits,
      usage: usageData.usage,
    };
    storage.set('userData', updatedUserData);

    document.dispatchEvent(new CustomEvent('membershipDataSynced', {
      detail: {
        userData: updatedUserData,
        current_tier: usageData.current_tier,
      },
    }));

    document.dispatchEvent(new CustomEvent('spriteCreditsUpdated', {
      detail: {
        sprite_credits: remainingSpriteCredits,
        current_tier: usageData.current_tier,
      },
    }));
  } catch (e) {
    console.warn('[videoScheduleApi] Failed to sync sprite credits:', e);
  }
};

/**
 * Backward-compatible alias for existing call sites.
 * @returns {Promise<void>}
 */
export const syncSpriteCredits = async () => syncMembershipUsage();

/**
 * Schedule a single video from text (motion prompt)
 * @param {{token: string, characterId: string|number, outfitId: string|number, actionId: number, actionText?: string}} params
 * @returns {Promise<{success: boolean, data?: any, error?: string}>}
 */
export const scheduleVideoFromTextRequest = async ({ token, characterId, outfitId, actionId, actionText }) => {
  if (!token) {
    return { success: false, error: 'No authentication token provided' };
  }

  if (!characterId || outfitId === undefined || outfitId === null) {
    return { success: false, error: 'Missing character or outfit ID' };
  }

  if (actionId === null || actionId === undefined) {
    return { success: false, error: 'Missing action ID' };
  }

  const response = await fetch(`${CREATE_SPRITE_WORKER_URL}/schedule-video-from-text`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify({
      character_id: characterId,
      outfit_id: outfitId,
      action_id: actionId,
      action_text: actionText ?? null,
    }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Request failed' }));
    return { success: false, error: error.message || error.error || 'Failed to schedule video' };
  }

  const data = await response.json().catch(() => ({}));
  return { success: true, data };
};

/**
 * Upgrade an existing low-res video (status 4) to high resolution.
 * @param {{token: string, characterId: string|number, outfitId: string|number, actionId: number}} params
 * @returns {Promise<{success: boolean, data?: any, error?: string}>}
 */
export const upgradeVideoResolutionRequest = async ({ token, characterId, outfitId, actionId }) => {
  if (!token) {
    return { success: false, error: 'No authentication token provided' };
  }

  if (!characterId || outfitId === undefined || outfitId === null) {
    return { success: false, error: 'Missing character or outfit ID' };
  }

  if (actionId === null || actionId === undefined) {
    return { success: false, error: 'Missing action ID' };
  }

  const response = await fetch(`${CREATE_SPRITE_WORKER_URL}/upgrade-video-resolution`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify({
      character_id: characterId,
      outfit_id: outfitId,
      action_id: actionId,
    }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Request failed' }));
    return { success: false, error: error.message || error.error || 'Failed to upgrade video' };
  }

  const data = await response.json().catch(() => ({}));
  return { success: true, data };
};

/**
 * Get auth token from oauthManager
 * @returns {Promise<string|null>}
 */
export const getAuthToken = async () => {
  try {
    const oauthManager = initOAuthManager();
    return await oauthManager.getValidToken();
  } catch (e) {
    console.error('[videoScheduleApi] getAuthToken error', e);
    return null;
  }
};

/**
 * Show free account modal when auth is required
 * @returns {Promise<void>}
 */
export const showFreeAccountModalIfNeeded = async () => {
  try {
    const token = await getAuthToken();
    if (token) return false;
    const { showFreeAccountModal } = await import('../components/FreeAccountModal.js');
    await showFreeAccountModal();
    return true;
  } catch (e) {
    console.error('[videoScheduleApi] showFreeAccountModal error', e);
    return false;
  }
};

/**
 * Read a create-sprite SSE status stream until completed, failed, or timeout.
 * @param {{url: string, token: string, timeoutMs: number, onComplete?: Function, onNotify?: Function}} opts
 * @returns {Promise<void>}
 */
export const waitForStatusStream = async ({ url, token, timeoutMs, onComplete, onNotify }) => {
  if (!url || !token) return;

  return new Promise((resolve) => {
    let abortController = null;
    let timeoutId = null;
    let settled = false;

    const finish = (status, data) => {
      if (settled) return;
      settled = true;
      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }
      if (abortController) {
        abortController.abort();
        abortController = null;
      }
      if (status === 'completed' && onComplete) onComplete();
      if (onNotify) onNotify(status, data);
      resolve();
    };

    timeoutId = setTimeout(() => finish('wait-timeout'), timeoutMs);

    try {
      abortController = new AbortController();
      fetch(url, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: 'text/event-stream',
        },
        signal: abortController.signal,
      }).then(async (response) => {
        if (!response.ok) throw new Error(`SSE request failed: ${response.status}`);

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        try {
          while (true) {
            const { done, value } = await reader.read();
            if (done) break;

            buffer += decoder.decode(value, { stream: true });
            const lines = buffer.split('\n');
            buffer = lines.pop() || '';

            for (const line of lines) {
              if (!line.startsWith('data: ')) continue;
              try {
                const data = JSON.parse(line.slice(6));
                if (data.status === 'completed') {
                  finish('completed', data);
                  return;
                }
                if (data.status === 'failed' || data.status === 'error' || data.status === 'timeout') {
                  finish(data.status, data);
                  return;
                }
              } catch (error) {
                console.error('[waitForStatusStream] Error parsing SSE message:', error);
              }
            }
          }
        } catch (error) {
          if (error.name !== 'AbortError') {
            console.error('[waitForStatusStream] Error reading SSE stream:', error);
          }
        } finally {
          reader.releaseLock();
        }
      }).catch((error) => {
        if (error.name === 'AbortError') return;
        console.error('[waitForStatusStream] SSE connection error:', error);
        finish('connection-error');
      });
    } catch (error) {
      console.error('[waitForStatusStream] Failed to create SSE connection:', error);
      finish('connection-error');
    }
  });
};

export const waitForSpriteCompletion = (charId, outfitId, actionId, token, onComplete, timeoutMs = 180000, onNotify) => {
  if (!charId || outfitId === '' || outfitId === undefined || actionId === null || actionId === undefined) {
    return Promise.resolve();
  }
  return waitForStatusStream({
    url: `${CREATE_SPRITE_WORKER_URL}/sprite-status-stream?char_id=${charId}&outfit_id=${outfitId}&action_id=${actionId}&timeout=${timeoutMs}`,
    token,
    timeoutMs,
    onComplete,
    onNotify,
  });
};

export const waitForVideoCompletion = (charId, outfitId, actionId, token, onComplete, timeoutMs = 300000, tier = 'lowres', onNotify) => {
  if (!charId || outfitId === '' || outfitId === undefined || actionId === null || actionId === undefined) {
    return Promise.resolve();
  }
  return waitForStatusStream({
    url: `${CREATE_SPRITE_WORKER_URL}/video-status-stream?char_id=${charId}&outfit_id=${outfitId}&action_id=${actionId}&timeout=${timeoutMs}&tier=${tier}`,
    token,
    timeoutMs,
    onComplete,
    onNotify,
  });
};
